import React from "react";
import { Dialog } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AccountDialogContent from "./accounts/AccountDialogContent";
import AddOrUpdateCategoryDialogComponent from "./categories/AddOrUpdateCategoryDialogComponent";
import ConnectionItemListWithCategoryDialogComponent from "./categories/ConnectionItemListWithCategoryDialogComponent";
import ManageCategoriesDialogComponent from "./categories/ManageCategoriesDialogComponent";
import ManageBusinessAccountsDialogComponent from "./accounts/ManageBusinessAccountsDialogComponent";
import AddOrUpdateBusinessAccountsDialogComponent from "./accounts/AddOrUpdateBusinessAccountsDialogComponent";
import ManageWebsiteConfigurationsDialogComponent from "./websiteConfigurations/ManageWebsiteConfigurationsDialogComponent";
import UpdateItemExtensionsDialogComponent from "./items/itemExtensions/UpdateItemExtensionsDialogComponent";
import UpdateItemAttributesDialogComponent from "./items/itemExtensions/UpdateItemAttributesDialogComponent";
import UpdateRelatedItemsDialogComponent from "./items/itemExtensions/UpdateRelatedItemsDialogComponent";
import ItemDetailsDialogComponent from "./items/ItemDetailsDialogComponent";
import ShoppingCartRecommendedItemsDialogComponent from "./shoppingCart/ShoppingCartRecommendedItemsDialogComponent";
import ManageAgentsDialogComponent from "./agents/ManageAgentsDialogComponent";
import AddOrUpdateAgentDialogComponent from "./agents/AddOrUpdateAgentDialogComponent";
import AssignAgentCustomersDialogComponent from "./agents/AssignAgentCustomersDialogComponent";
import SelectCustomerDialogComponent from "./agents/SelectCustomerDialogComponent";
import UpdateItemGroupImageDialogComponent from "./itemGroups/UpdateItemGroupImageDialogComponent";

// Dialog routes and actions as an object
const dialogsRoutes = {
  account: {
    manage: ManageBusinessAccountsDialogComponent,
    login: AccountDialogContent,
    add: AddOrUpdateBusinessAccountsDialogComponent,
  },
  // 0023: the customer-picker flow moved from a full page to this dialog (?d=customer&action=select),
  // opened from both the 0017 bar and the agent/admin dashboard card.
  customer: {
    select: SelectCustomerDialogComponent,
  },
  agent: {
    manage: ManageAgentsDialogComponent,
    add: AddOrUpdateAgentDialogComponent,
    assign: AssignAgentCustomersDialogComponent,
  },
  item: {
    default: ItemDetailsDialogComponent,
  },
  shoppingCart: {
    recommendedItems: ShoppingCartRecommendedItemsDialogComponent,
  },
  itemExtension: {
    edit: UpdateItemExtensionsDialogComponent,
    attributes: UpdateItemAttributesDialogComponent,
    relatedItems: UpdateRelatedItemsDialogComponent,
  },
  category: {
    manage: ManageCategoriesDialogComponent,
    add: AddOrUpdateCategoryDialogComponent,
    update: AddOrUpdateCategoryDialogComponent,
    connect: ConnectionItemListWithCategoryDialogComponent,
  },
  // 0036: item-group image upload, opened per row from catalog management
  // (?d=itemGroup&action=image&id=<itemGroup guid>).
  itemGroup: {
    image: UpdateItemGroupImageDialogComponent,
  },
  websiteComfiguration: {
    manage: ManageWebsiteConfigurationsDialogComponent,
  },
};

export const DialogManagerComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogType = searchParams.get("d"); // Get the dialog type from the query parameter
  const actionType = searchParams.get("action") || "default"; // Get the action type or use "default"

  // Extract the current route and action
  const currentRoute = Object.keys(dialogsRoutes).find((route) => dialogType === route);
  const currentAction = dialogsRoutes[currentRoute]?.[actionType];

  // Check if the dialog should be open
  const isDialogOpen = !!currentRoute && !!currentAction;

  const getDialogContentComponent = () => {
    // Get the content component based on the route and action
    if (!currentRoute || !currentAction) return null;

    const DialogContentComponent = currentAction;
    return DialogContentComponent ? <DialogContentComponent /> : null;
  };

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Remove the dialog-related query parameters
    newSearchParams.delete("d");
    newSearchParams.delete("action");
    newSearchParams.delete("itemId");
    newSearchParams.delete("id");
    // Update the URL with the modified search parameters
    setSearchParams(newSearchParams);
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      maxWidth={false}
      sx={{ zIndex: 1000 }}
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      <div className="dialogPaperWrapper">
        <div className="closeDialog" onClick={handleClose}>
          <i className="fa-solid fa-xmark"></i>
        </div>
        {getDialogContentComponent()}
      </div>
    </Dialog>
  );
};

export default DialogManagerComponent;
